import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  addCommonType,
  deleteCommonGoods,
  deleteCommonType,
  getCommonLists,
  type CommonUseGoods,
  type CommonUseType,
} from '@/api/common';
import { useCart } from '@/hooks/useCart';
import ConfirmModal from '@/pages/orders/components/ConfirmModal';

interface CategoryView {
  id: string;
  name: string;
  itemCount: number;
  items: ItemView[];
}

interface ItemView {
  id: string;
  productId: string;
  specId: string;
  name: string;
  model: string;
  quantity: number;
  unitPrice: number;
  unit: string;
  image: string;
  stock: number;
}

function mapGoodsItem(row: CommonUseGoods): ItemView {
  const price = Number(row.spec?.price ?? 0);
  const stockRaw = row.spec?.stock;
  const stock = stockRaw == null || stockRaw === '' ? -1 : Number(stockRaw);
  return {
    id: String(row.id),
    productId: String(row.goods_id || row.goods?.id || ''),
    specId: String(row.spec_id || row.spec?.id || ''),
    name: row.goods_name || row.goods?.title || '未命名物资',
    model: row.spec_name || row.spec?.name || '',
    quantity: Math.max(1, Number(row.number ?? 1) || 1),
    unitPrice: Number.isFinite(price) ? price : 0,
    unit: row.spec?.unit || '件',
    image: row.goods?.m_pic || '',
    stock: Number.isFinite(stock) ? stock : -1,
  };
}

function mapCategory(row: CommonUseType): CategoryView {
  const items = Array.isArray(row.goods) ? row.goods.map(mapGoodsItem) : [];
  return {
    id: String(row.id),
    name: String(row.name || '未命名分类'),
    itemCount: items.length,
    items,
  };
}

export default function FrequentPurchase() {
  const { addItem } = useCart();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [editQuantities, setEditQuantities] = useState<Record<string, number>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingType, setDeletingType] = useState(false);
  const [showDeleteTypeConfirm, setShowDeleteTypeConfirm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [categories, setCategories] = useState<CategoryView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const openCreateModal = () => {
    setCreateName('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    setCreateName('');
  };

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getCommonLists();
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setLoadError(res.message || res.msg || '常购分类加载失败');
        setCategories([]);
        setSelectedCategoryId(null);
        return;
      }

      const mapped = (Array.isArray(res.data) ? res.data : []).map(mapCategory);
      setCategories(mapped);

      setSelectedCategoryId((prev) => {
        if (prev && mapped.some((c) => c.id === prev)) return prev;
        return mapped[0]?.id ?? null;
      });
    } catch {
      setLoadError('常购分类加载失败，请稍后重试');
      setCategories([]);
      setSelectedCategoryId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLists();
  }, [fetchLists]);

  const handleCreateCategory = async () => {
    const name = createName.trim();
    if (!name) {
      showToast('请输入分类名称');
      return;
    }
    if (creating) return;
    setCreating(true);
    try {
      const res = await addCommonType({ name });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '创建失败');
        return;
      }
      showToast(res.message || '添加常用分类成功');
      setShowCreateModal(false);
      setCreateName('');
      const newId = res.data?.id != null ? String(res.data.id) : null;
      await fetchLists();
      if (newId) setSelectedCategoryId(newId);
    } catch {
      showToast('创建失败，请稍后重试');
    } finally {
      setCreating(false);
    }
  };

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId],
  );

  const categoryItems = selectedCategory?.items ?? [];

  useEffect(() => {
    if (!selectedCategory) {
      setEditQuantities({});
      return;
    }
    const qtyMap: Record<string, number> = {};
    selectedCategory.items.forEach((item) => {
      qtyMap[item.id] = item.quantity;
    });
    setEditQuantities(qtyMap);
  }, [selectedCategory]);

  const handleSelectCategory = (cat: CategoryView) => {
    setSelectedCategoryId(cat.id);
  };

  const handleQuantityChange = (itemId: string, delta: number) => {
    setEditQuantities((prev) => ({
      ...prev,
      [itemId]: Math.max(0, (prev[itemId] || 0) + delta),
    }));
  };

  const handleRemoveCategoryItem = async (itemId: string) => {
    if (deletingId) return;
    setDeletingId(itemId);
    try {
      const res = await deleteCommonGoods(itemId);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除失败');
        return;
      }
      showToast(res.message || '删除成功');
      await fetchLists();
    } catch {
      showToast('删除失败，请稍后重试');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategoryId || deletingType) return;
    setDeletingType(true);
    try {
      const res = await deleteCommonType(selectedCategoryId);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除分类失败');
        return;
      }
      showToast(res.message || '删除成功');
      setShowDeleteTypeConfirm(false);
      setSelectedCategoryId(null);
      await fetchLists();
    } catch {
      showToast('删除分类失败，请稍后重试');
    } finally {
      setDeletingType(false);
    }
  };

  const handleAddAllToCart = () => {
    let added = 0;
    categoryItems.forEach((item) => {
      const qty = editQuantities[item.id] || 0;
      if (qty > 0 && item.productId) {
        for (let i = 0; i < qty; i++) {
          addItem({
            productId: item.productId,
            name: item.name,
            price: item.unitPrice,
            unit: item.unit,
            image: item.image,
            stock: item.stock,
            model: item.model,
            specId: item.specId || undefined,
            specName: item.model || undefined,
          });
        }
        added += qty;
      }
    });
    if (added > 0) {
      showToast(`已将 ${added} 件物资加入购物车`);
    }
  };

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-medium shadow-lg flex items-center gap-2">
          <i className="ri-checkbox-circle-line" />
          {toast}
        </div>
      )}

      <div className="px-4 pb-4 md:px-6 md:pb-6">
        <div className="flex flex-row gap-6">
          {/* Category list */}
          <div className="w-72 shrink-0">
            <div className="bg-white rounded-xl border border-background-200 p-4">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">常购分类</h3>
              {loading ? (
                <p className="text-sm text-foreground-400 py-4 text-center">
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  加载中...
                </p>
              ) : loadError ? (
                <div className="py-4 text-center">
                  <p className="text-sm text-foreground-500 mb-2">{loadError}</p>
                  <button
                    type="button"
                    onClick={() => void fetchLists()}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    重新加载
                  </button>
                </div>
              ) : categories.length === 0 ? (
                <p className="text-sm text-foreground-400 py-4 text-center">暂无常购分类</p>
              ) : (
                <div className="space-y-1 mb-3">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleSelectCategory(cat)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        selectedCategory?.id === cat.id
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{cat.name}</span>
                        <span className="text-xs opacity-70">{cat.itemCount} 件</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={openCreateModal}
                className="w-full py-2.5 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line mr-1" />
                新建常购分类
              </button>
            </div>
          </div>

          {/* Items */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="bg-white rounded-xl border border-background-200 p-12 flex flex-col items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400 mb-3" />
                <p className="text-sm text-foreground-500">正在加载常购清单...</p>
              </div>
            ) : !selectedCategory ? (
              <div className="bg-white rounded-xl border border-background-200 p-12 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shopping-basket-line text-2xl text-foreground-400" />
                </div>
                <p className="text-sm text-foreground-500 mb-2">请选择左侧常购分类</p>
                <p className="text-xs text-foreground-400">常购单帮助您快速重复采购常用物资</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-background-200 p-5">
                <div className="flex items-center justify-between mb-4 gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900">
                      {selectedCategory.name} · 常购清单
                    </h3>
                    <span className="text-xs text-foreground-500">{categoryItems.length} 件物资</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowDeleteTypeConfirm(true)}
                      disabled={deletingType}
                      className="px-4 py-2 rounded-lg border border-primary-200 text-primary-600 font-medium hover:bg-primary-50 transition-colors whitespace-nowrap text-sm disabled:opacity-50"
                    >
                      <i className="ri-delete-bin-line mr-1" />
                      删除分类
                    </button>
                    {categoryItems.length > 0 && (
                      <button
                        type="button"
                        onClick={handleAddAllToCart}
                        className="px-4 py-2 rounded-lg border-2 border-primary-500 text-primary-600 font-medium hover:bg-primary-50 transition-colors whitespace-nowrap text-sm"
                      >
                        <i className="ri-shopping-cart-line mr-1" />
                        加入购物车
                      </button>
                    )}
                  </div>
                </div>

                {categoryItems.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-sm text-foreground-400">此分类暂无物资</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {categoryItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 py-3 border-b border-background-100 last:border-0"
                      >
                        <div className="w-14 h-14 bg-background-100 rounded-lg overflow-hidden shrink-0">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-foreground-300">
                              <i className="ri-image-line text-xl" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 line-clamp-1">{item.name}</p>
                          <p className="text-xs text-foreground-500">
                            {item.model || '-'} · ¥{item.unitPrice.toFixed(2)}/{item.unit}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-foreground-400">数量：</span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(item.id, -1)}
                            className="w-7 h-7 rounded border border-background-300 flex items-center justify-center text-foreground-600 hover:bg-background-100"
                          >
                            <i className="ri-subtract-line text-xs" />
                          </button>
                          <span className="text-sm font-medium w-8 text-center">
                            {editQuantities[item.id] || 0}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(item.id, 1)}
                            className="w-7 h-7 rounded bg-primary-500 flex items-center justify-center text-background-50 hover:bg-primary-600"
                          >
                            <i className="ri-add-line text-xs" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => void handleRemoveCategoryItem(item.id)}
                          disabled={deletingId === item.id}
                          className="w-7 h-7 rounded flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors shrink-0 disabled:opacity-50"
                          title="移除此物资"
                        >
                          <i
                            className={`text-sm ${
                              deletingId === item.id
                                ? 'ri-loader-4-line animate-spin'
                                : 'ri-delete-bin-line'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <ConfirmModal
          isOpen={showDeleteTypeConfirm}
          title="删除常购分类"
          message={`确定删除分类「${selectedCategory?.name || ''}」吗？分类下的物资将一并删除。`}
          confirmLabel="确认删除"
          danger
          loading={deletingType}
          onCancel={() => {
            if (!deletingType) setShowDeleteTypeConfirm(false);
          }}
          onConfirm={() => void handleDeleteCategory()}
        />

        {/* Create modal */}
        {showCreateModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={closeCreateModal}
          >
            <div
              className="bg-background-50 rounded-xl w-full max-w-sm mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-background-200">
                <h3 className="text-sm font-semibold text-foreground-900">新建常购分类</h3>
              </div>
              <div className="px-5 py-4">
                <label className="text-xs text-foreground-500 mb-1 block">分类名称</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value.slice(0, 50))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleCreateCategory();
                  }}
                  disabled={creating}
                  placeholder="例如：月度办公耗材"
                  maxLength={50}
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none focus:border-primary-400 disabled:bg-background-100"
                />
              </div>
              <div className="px-5 py-4 border-t border-background-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={creating}
                  className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreateCategory()}
                  disabled={creating || !createName.trim()}
                  className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? '创建中...' : '创建'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
